import { useCallback, useRef, useState } from 'react';
import { useAuthContext } from '@/context/AuthContext';
import { ROLES } from '@/constants';
import { useChatPanel } from '@/features/chat/context/ChatContext';
import { useOpenChatConversation } from '@/features/chat/hooks/useOpenChatConversation';
import { leadershipService } from '@/features/leadership/services/leadershipService';
import { buildOverdueTaskReplyPreview } from '@/features/leadership/utils/overdueTaskReplyPreview';

const EXEC_LIKE_ROLES = new Set([ROLES.EXECUTIVE, ROLES.ADMIN, ROLES.SUPER_ADMIN]);

export function useOverdueNotificationReply() {
    const { user } = useAuthContext();
    const { openPanel, setPendingComposerDraft } = useChatPanel();
    const { openByMatcher } = useOpenChatConversation();
    const taskForcesCacheRef = useRef(null);
    const [replyError, setReplyError] = useState(null);

    const loadTaskForcesForExecutive = useCallback(async () => {
        if (taskForcesCacheRef.current) return taskForcesCacheRef.current;
        const { taskForces } = await leadershipService.getExecutiveDashboard();
        const list = Array.isArray(taskForces) ? taskForces : [];
        taskForcesCacheRef.current = list;
        return list;
    }, []);

    const clearReplyError = useCallback(() => setReplyError(null), []);

    /**
     * @param {{
     *   taskId: string,
     *   taskName?: string,
     *   taskDetail: object,
     *   taskForceId: string,
     *   pillarId: string,
     *   pillarName?: string,
     *   taskForceName?: string,
     * }} ctx
     */
    const replyToOverdueTask = useCallback(
        async (ctx) => {
            setReplyError(null);
            const role = user?.role;
            const {
                taskId,
                taskName,
                taskDetail,
                taskForceId,
                pillarId,
                pillarName,
                taskForceName,
            } = ctx;

            const preview = buildOverdueTaskReplyPreview({
                task: taskDetail,
                pillarName,
                taskForceName,
            });

            if (EXEC_LIKE_ROLES.has(role)) {
                if (!taskForceId) {
                    setReplyError(
                        'This task has no task force context. Open Messages and select the task force chair chat manually.',
                    );
                    return { ok: false };
                }
                const taskForces = await loadTaskForcesForExecutive();
                const tf = taskForces.find((x) => String(x?.id ?? '') === String(taskForceId));
                const chairUserId = tf?.account?.user?.id ?? tf?.chairUserId ?? null;
                const tfName = tf?.name ?? taskForceName ?? null;

                openPanel();
                const { ok, conversation } = await openByMatcher((c) => {
                    if (c?.kind !== 'direct') return false;
                    if (
                        chairUserId &&
                        Array.isArray(c?.participantIds) &&
                        c.participantIds.some((id) => String(id) === String(chairUserId))
                    ) {
                        return true;
                    }
                    const scopeType = String(c?.scopeType ?? c?.scope_type ?? '').trim();
                    const scopeId = String(c?.scopeId ?? c?.scope_id ?? '').trim();
                    if (scopeType === 'task_force' && scopeId !== '' && scopeId === String(taskForceId)) {
                        return true;
                    }
                    if (tfName) {
                        const subtitle = String(c?.subtitle ?? '').toLowerCase();
                        const name = String(tfName).toLowerCase();
                        if (subtitle && name && subtitle.includes(name)) return true;
                    }
                    return false;
                });
                if (!ok || !conversation?.id) {
                    setReplyError(
                        'Could not open the direct chat for this task force chair. Open Messages and pick the correct chat.',
                    );
                    return { ok: false };
                }
                setPendingComposerDraft({
                    replyPreview: preview,
                    tag: 'overdue-task-exec',
                    conversationId: conversation.id,
                });
                return { ok: true };
            }

            if (role === ROLES.TASK_FORCE_CHAIR) {
                if (!pillarId) {
                    setReplyError(
                        'This task has no pillar context. Open Messages and select the pillar chair chat manually.',
                    );
                    return { ok: false };
                }
                openPanel();
                const { ok, conversation } = await openByMatcher(
                    (c) =>
                        c?.kind === 'direct' &&
                        String(c?.scopeType ?? '').trim() === 'pillar' &&
                        String(c?.scopeId ?? '') === String(pillarId),
                );
                if (!ok || !conversation?.id) {
                    setReplyError(
                        'Could not open the pillar chair chat. It may be unavailable yet. Open Messages and select it from the list.',
                    );
                    return { ok: false };
                }
                setPendingComposerDraft({
                    replyPreview: preview,
                    tag: 'overdue-task-tf',
                    conversationId: conversation.id,
                });
                return { ok: true };
            }

            if (role === ROLES.PILLAR_CHAIR) {
                const taskScopeId = taskId != null ? String(taskId) : '';
                if (!taskScopeId) {
                    setReplyError('Missing task id for chat.');
                    return { ok: false };
                }
                const label = String(
                    taskDetail?.name ?? taskName ?? '',
                )
                    .trim()
                    .toLowerCase();

                openPanel();
                const { ok, conversation } = await openByMatcher((c) => {
                    if (c?.kind !== 'group') return false;
                    const scopeType = String(c?.scopeType ?? c?.scope_type ?? '').trim();
                    const scopeId = String(c?.scopeId ?? c?.scope_id ?? '').trim();
                    if (scopeType === 'task' && scopeId !== '' && scopeId === taskScopeId) {
                        return true;
                    }
                    const name = String(c?.name ?? '').trim().toLowerCase();
                    const subtitle = String(c?.subtitle ?? '').trim().toLowerCase();
                    const token = taskScopeId.trim().toLowerCase();
                    const looksLikeTaskThread =
                        name.startsWith('task:') || subtitle === 'task-specific' || subtitle.includes('task-specific');
                    if (!looksLikeTaskThread) return false;
                    if (token && (name.includes(token) || subtitle.includes(token))) return true;
                    if (label.length >= 6) {
                        return name.includes(label) || subtitle.includes(label);
                    }
                    return false;
                });
                if (!ok || !conversation?.id) {
                    setReplyError(
                        'Could not open the task group chat. Open Messages and pick the task thread from the list.',
                    );
                    return { ok: false };
                }
                setPendingComposerDraft({
                    replyPreview: preview,
                    tag: 'overdue-task-pillar',
                    conversationId: conversation.id,
                });
                return { ok: true };
            }

            setReplyError('Reply in chat is not set up for this role.');
            return { ok: false };
        },
        [loadTaskForcesForExecutive, openByMatcher, openPanel, setPendingComposerDraft, user?.role],
    );

    return { replyToOverdueTask, replyError, setReplyError, clearReplyError };
}
